import React from 'react';

interface CertificatePreviewProps {
  monumentName?: string;
  dedicateeName?: string;
  dedicationMessage?: string;
  location?: string;
  showSample?: boolean;
}

const CertificatePreview: React.FC<CertificatePreviewProps> = ({
  monumentName = 'Monument Name',
  dedicateeName = 'Dedicatee Name',
  dedicationMessage = 'Your dedication message will appear here...',
  location = 'Coordinates will be assigned',
  showSample = false,
}) => {
  const registryNumber = React.useMemo(() => 
    // eslint-disable-next-line react-hooks/purity
    showSample ? 'YU-SPO-2025-####' : `YU-SPO-2025-${Math.floor(Math.random() * 9000) + 1000}`,
    [showSample]
  );
  
  const serialNumber = React.useMemo(() => 
    // eslint-disable-next-line react-hooks/purity
    showSample ? 'BC-####-VSPOM-2025' : `BC-${Math.floor(Math.random() * 9000) + 1000}-VSPOM-2025`,
    [showSample]
  );

  const securityCode = React.useMemo(() => 
    // eslint-disable-next-line react-hooks/purity
    Math.random().toString(36).substring(2, 8).toUpperCase(),
    []
  );

  const todayDate = new Date().toLocaleDateString('en-GB');

  return (
    <div className="bg-gradient-to-br from-amber-50 to-amber-100 border-8 border-[#8B4513] p-8 md:p-12 shadow-2xl font-serif relative">
      {/* Watermark */}
      <div className="absolute inset-0 flex items-center justify-center opacity-5 pointer-events-none">
        <svg className="w-64 h-64" viewBox="0 0 100 100">
          <polygon points="50,10 61,40 92,40 67,59 78,89 50,70 22,89 33,59 8,40 39,40" />
        </svg>
      </div>

      {/* Red Border Frame */}
      <div className="border-4 border-[#C41E3A] p-6 md:p-10 bg-white relative">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <svg className="w-16 h-16 fill-[#C41E3A]" viewBox="0 0 100 100">
              <polygon points="50,10 61,40 92,40 67,59 78,89 50,70 22,89 33,59 8,40 39,40" />
            </svg>
          </div>
          <div className="text-xs font-bold uppercase tracking-widest mb-2">
            Socialist Federal Republic of Yugoslavia
          </div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-700">
            Federal Committee for Monuments and Antifascist Heritage
          </div>
          <div className="border-t-2 border-b-2 border-black my-4 py-2">
            <h1 className="text-xl md:text-2xl font-black uppercase tracking-tight">
              Certificate of Monument Erection
            </h1>
          </div>
        </div>

        {/* Document Info */}
        <div className="mb-6 text-xs font-mono">
          <div className="flex justify-between mb-1">
            <span className="font-bold">Registry No:</span>
            <span className="font-black">{registryNumber}</span>
          </div>
          <div className="flex justify-between">
            <span className="font-bold">Classification:</span>
            <span className="font-black">VIRTUAL SPOMENIK - CLASS A</span>
          </div>
        </div>

        <div className="border-t-2 border-b-2 border-gray-400 my-6"></div>

        {/* Main Content */}
        <div className="mb-8 text-sm leading-relaxed">
          <p className="mb-4 text-justify">
            By the power vested in the Federal Committee, and in accordance with Resolution 1974/42
            on National Heritage, this certificate hereby authorizes the erection of:
          </p>

          <div className="bg-gray-50 border-2 border-black p-4 mb-4 space-y-2">
            <div>
              <span className="font-bold uppercase text-xs">Monument Name:</span>
              <div className="font-black text-base mt-1">{monumentName}</div>
            </div>
            <div>
              <span className="font-bold uppercase text-xs">Dedicated To:</span>
              <div className="font-black text-base mt-1">{dedicateeName}</div>
            </div>
            <div>
              <span className="font-bold uppercase text-xs">Coordinates:</span>
              <div className="font-mono text-sm mt-1">{location}</div>
            </div>
            <div>
              <span className="font-bold uppercase text-xs">Erected:</span>
              <div className="font-mono text-sm mt-1">{todayDate}</div>
            </div>
          </div>

          <div className="mb-4">
            <div className="font-bold uppercase text-xs mb-2">Dedication:</div>
            <div className="bg-amber-50 border-2 border-gray-300 p-3 italic text-sm min-h-[60px]">
              {dedicationMessage}
            </div>
          </div>

          <p className="text-justify italic text-xs text-gray-700">
            This monument stands eternal in the virtual memorial landscape of the people&apos;s remembrance.
          </p>
        </div>

        <div className="border-t-2 border-gray-400 pt-6"></div>

        {/* Signatures and Stamps */}
        <div className="grid grid-cols-2 gap-8 mb-6">
          {/* Official Stamp */}
          <div className="text-center">
            <div className="inline-block relative">
              <div className="w-24 h-24 rounded-full border-4 border-[#C41E3A] flex items-center justify-center bg-red-50 rotate-12">
                <div className="text-center">
                  <div className="text-[8px] font-black uppercase">APPROVED</div>
                  <div className="text-[6px] font-bold">{todayDate}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Tito's Signature */}
          <div className="text-center">
            <div className="mb-2">
              <div className="text-2xl font-script italic">Josip Broz Tito</div>
              <div className="h-px bg-black w-40 mx-auto mb-1"></div>
            </div>
            <div className="text-[10px] font-bold uppercase">President for Life</div>
          </div>
        </div>

        {/* Multiple Stamps Row */}
        <div className="flex justify-around items-center mb-6 flex-wrap gap-4">
          {/* Stamp 1 */}
          <div className="w-16 h-16 rounded-full border-2 border-black flex items-center justify-center bg-blue-50 -rotate-6 opacity-60">
            <div className="text-[6px] font-black uppercase text-center">
              DEPT<br/>SEAL
            </div>
          </div>

          {/* Stamp 2 */}
          <div className="w-20 h-12 border-2 border-purple-700 flex items-center justify-center bg-purple-50 rotate-3 opacity-60">
            <div className="text-[6px] font-black uppercase text-center text-purple-900">
              VERIFIED<br/>{todayDate}
            </div>
          </div>

          {/* Stamp 3 */}
          <div className="w-16 h-16 rounded-full border-2 border-green-700 flex items-center justify-center bg-green-50 rotate-12 opacity-60">
            <div className="text-[6px] font-black uppercase text-center text-green-900">
              OFFICIAL
            </div>
          </div>

          {/* Wax Seal */}
          <div className="w-14 h-14 rounded-full bg-[#C41E3A] border-2 border-[#8B1E1E] flex items-center justify-center shadow-lg">
            <svg className="w-8 h-8 fill-white opacity-80" viewBox="0 0 100 100">
              <polygon points="50,10 61,40 92,40 67,59 78,89 50,70 22,89 33,59 8,40 39,40" />
            </svg>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t-2 border-gray-300 pt-4 flex justify-between text-[8px] font-mono text-gray-600">
          <div>Serial: {serialNumber}</div>
          <div>Security Code: {securityCode}</div>
        </div>

        {/* Bottom Disclaimer */}
        <div className="mt-6 text-[8px] text-center text-gray-500 italic border-t pt-2">
          *Virtual monument. For commemorative purposes only. Not an official government document.
        </div>
      </div>

      {/* Vintage Paper Corners */}
      <div className="absolute top-2 left-2 w-8 h-8 border-l-4 border-t-4 border-amber-700 opacity-30"></div>
      <div className="absolute top-2 right-2 w-8 h-8 border-r-4 border-t-4 border-amber-700 opacity-30"></div>
      <div className="absolute bottom-2 left-2 w-8 h-8 border-l-4 border-b-4 border-amber-700 opacity-30"></div>
      <div className="absolute bottom-2 right-2 w-8 h-8 border-r-4 border-b-4 border-amber-700 opacity-30"></div>
    </div>
  );
};

export default CertificatePreview;
